import { IconInfoCircle } from '@tabler/icons-react';
import { IconPhoto } from '@tabler/icons-react';
import { useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

type ProfileImagesInputProps = {
  profileImageUrl?: string | null;
  coverImageUrl?: string | null;
  description?: string;
  profileErrors?: string[];
  coverErrors?: string[];
  accept?: string;
  disabled?: boolean;
  onProfileSelect: (file: File | null) => void;
  onCoverSelect: (file: File | null) => void;
  onCoverRemove: () => void;
};

export const ProfileImagesInput = ({
  profileImageUrl,
  coverImageUrl,
  description,
  profileErrors,
  coverErrors,
  accept,
  disabled = false,
  onProfileSelect,
  onCoverSelect,
  onCoverRemove,
}: ProfileImagesInputProps) => {
  const { t } = useTranslation();
  const profileInputRef = useRef<HTMLInputElement>(null);
  const coverInputRef = useRef<HTMLInputElement>(null);
  const profileInputId = useId();
  const coverInputId = useId();
  const [failedProfileImageUrl, setFailedProfileImageUrl] = useState<
    string | null
  >(null);
  const [failedCoverImageUrl, setFailedCoverImageUrl] = useState<string | null>(
    null
  );
  const profileImageSrc = profileImageUrl ?? undefined;
  const coverImageSrc = coverImageUrl ?? undefined;
  const shouldShowProfileImage =
    profileImageSrc && failedProfileImageUrl !== profileImageSrc;
  const shouldShowCoverImage =
    coverImageSrc && failedCoverImageUrl !== coverImageSrc;

  return (
    <div className="space-y-3">
      <input
        id={coverInputId}
        ref={coverInputRef}
        type="file"
        accept={accept}
        disabled={disabled}
        className="hidden"
        onChange={(event) => {
          onCoverSelect(event.currentTarget.files?.[0] ?? null);
          event.currentTarget.value = '';
        }}
      />
      <input
        id={profileInputId}
        ref={profileInputRef}
        type="file"
        accept={accept}
        disabled={disabled}
        className="hidden"
        onChange={(event) => {
          onProfileSelect(event.currentTarget.files?.[0] ?? null);
          event.currentTarget.value = '';
        }}
      />

      <div className="relative pb-9">
        <button
          type="button"
          className="group relative h-32 w-full overflow-hidden rounded-xl border border-dashed border-gray-300 bg-gray-50 text-left sm:h-36"
          aria-label={t('カバー画像')}
          disabled={disabled}
          onClick={() => coverInputRef.current?.click()}
        >
          {shouldShowCoverImage ? (
            <img
              src={coverImageSrc}
              alt={t('カバー画像')}
              className="size-full object-cover"
              onError={() => setFailedCoverImageUrl(coverImageSrc)}
            />
          ) : (
            <div className="flex size-full items-center justify-center gap-2 text-xs text-gray-500">
              <IconPhoto className="size-5" />
              <span>{t('画像を選択')}</span>
            </div>
          )}

          <div className="absolute inset-0 bg-black/0 transition-colors group-hover:bg-black/15 group-focus-visible:bg-black/15" />
        </button>
        {coverImageUrl && (
          <button
            type="button"
            className="absolute right-2 top-2 grid size-7 place-items-center rounded-full bg-black/60 text-lg leading-none text-white ring-1 ring-white/70 transition-colors hover:bg-black/75"
            aria-label={t('削除')}
            disabled={disabled}
            onClick={onCoverRemove}
          >
            ×
          </button>
        )}

        <button
          type="button"
          className="group absolute bottom-0 left-4 size-20 overflow-hidden rounded-full border-4 border-white bg-gray-50 text-left shadow-sm sm:left-5 sm:size-24"
          aria-label={t('プロフィール画像')}
          disabled={disabled}
          onClick={() => profileInputRef.current?.click()}
        >
          {shouldShowProfileImage ? (
            <img
              src={profileImageSrc}
              alt={t('プロフィール画像')}
              className="size-full object-cover"
              onError={() => setFailedProfileImageUrl(profileImageSrc)}
            />
          ) : (
            <div className="flex size-full flex-col items-center justify-center gap-1 px-2 text-center text-xs text-gray-500">
              <IconPhoto className="size-5" />
              <span>{t('画像を選択')}</span>
            </div>
          )}

          <div className="absolute inset-0 rounded-full bg-black/0 transition-colors group-hover:bg-black/15 group-focus-visible:bg-black/15" />
        </button>
      </div>

      <div className="space-y-2 text-xs leading-relaxed text-gray-500">
        {description && <p>{description}</p>}
        <ImageErrors errors={profileErrors} />
        <ImageErrors errors={coverErrors} />
      </div>
    </div>
  );
};

const ImageErrors = ({ errors }: { errors?: string[] }) => (
  <>
    {errors?.map((message) => (
      <div key={message} className="flex items-start text-sm text-red-600">
        <IconInfoCircle className="mr-2 mt-0.5 size-4 shrink-0" />
        <span>{message}</span>
      </div>
    ))}
  </>
);
